import { useToast } from '@chakra-ui/react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import api, { loginCustomerService, registerDevice, updateCustomerService } from 'services/api.service';
import jwt_decode from 'jwt-decode';
import { Capacitor } from '@capacitor/core';
import OneSignal from 'onesignal-cordova-plugin';
import { DateTime } from 'luxon';
import { Device } from '@capacitor/device';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';

export const AUTHSTORAGE_KEY = `asdasd1as32d1as6d54wq6d132dsg1r`;

type AuthContextType = {
  userData: AuthUserData;
  signIn: (args: {
    username: string;
    token?: string;
    history: any;
    password: string;
    callbackUrl?: string;
    captchaKey?: string;
    turnstileToken?: string;
  }) => Promise<AuthUserData>;
  signOut: (history: any) => Promise<void>;
  updateUserData: () => void;
};

export type AuthUserData = {
  isAuth: boolean;
  authorization: string;
  name: string;
  email: string;
  id: string;
  balance: number;
  amountLocked: number;
  accountNumber: string;
  balancePending: number;
  on: string;
  withdrawalApprovalsRequired?: number;
  features: {
    pix: boolean;
    billet: boolean;
    transfer: boolean;
    withdraw: boolean;
    deposit?: boolean;
    allowCardRecharge?: boolean;
    charges?: boolean;
  };
};

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export default function useAuth() {
  return useContext(AuthContext);
}

export function getAuthStorage(): AuthUserData {
  if (typeof localStorage === `undefined`) {
    console.log(`localStorage is not defined`);
    return {} as AuthUserData;
  }
  try {
    const localStorageData = localStorage.getItem(AUTHSTORAGE_KEY);

    return localStorageData ? JSON.parse(localStorageData) : ({ isAuth: false } as AuthUserData);
  } catch (error) {
    console.error(`localStorage get error`, error);
    return {} as AuthUserData;
  }
}

export function setAuthStorage(value: AuthUserData): void {
  return localStorage.setItem(AUTHSTORAGE_KEY, JSON.stringify(value));
}

export function delAuthStorage(): void {
  localStorage.removeItem(AUTHSTORAGE_KEY);
  localStorage.removeItem('Authorization');
}

export function AuthProvider({ children }: any) {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [userData, setUserData] = useState<AuthUserData | null>(getAuthStorage() ?? ({} as AuthUserData));

  const storeUserData = async (data: {
    authorization: string;
    email: string;
    isAuth: boolean;
    id: string;
    name: string;
    balance: number;
    amountLocked: number;
    balancePending: number;
    accountNumber: string;
    features: any;
  }) => {
    setUserData({ ...data, on: DateTime.now().toISO() });
    setAuthStorage({ ...data, on: DateTime.now().toISO() });
    localStorage.setItem('Authorization', data.authorization);
    api.defaults.headers.Authorization = 'Bearer ' + data.authorization;
  };

  const signIn = useCallback(
    async ({
      username,
      password,
      token,
      history,
      callbackUrl = `/access/dashboard`,
      captchaKey,
      turnstileToken,
    }: {
      username: string;
      history: any;
      token?: string;
      password: string;
      callbackUrl?: string;
      captchaKey?: string;
      turnstileToken?: string;
    }): Promise<AuthUserData> => {
      const authResponse = await loginCustomerService(username, password, captchaKey, token, turnstileToken);
      toast({
        title: 'Sucesso',
        description: 'Login realizado com sucesso',
        status: 'success',
        duration: 5000,
        position: 'top',
        isClosable: true,
      });

      const data = {
        authorization: authResponse.token,
        email: authResponse.email,
        isAuth: true,
        id: authResponse.id,
        name: authResponse.name,
        balance: authResponse.balance,
        amountLocked: authResponse.amountLocked,
        balancePending: authResponse.balancePending,
        features: authResponse.features,
        twoFaEnabled: authResponse.twoFaEnabled,
        accountNumber: authResponse.accountNumber,
        on: DateTime.now().toISO(),
      };
      await storeUserData(data);

      if (Capacitor.isNativePlatform()) {
        OneSignal.login(authResponse.id);
        const id = await Device.getId();
        await registerDevice(id.identifier).catch((error) => {
          console.error(error);
        });
      }

      navigate(callbackUrl);
      return data;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate],
  );

  const signOut = useCallback(async (history: any) => {
    try {
      setUserData({} as AuthUserData);
      setAuthStorage({} as AuthUserData);
      delAuthStorage();
      api.defaults.headers.Authorization = null;
      navigate(`/auth/sign-in`);
    } catch (error) {
      console.error(error);
      throw error;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    const handleLogout = () => {
      console.log('handleLogout', userData);

      delAuthStorage();
      setUserData({} as AuthUserData);
      navigate('/auth/sign-in');
    };
    window.addEventListener('auth:logout', handleLogout);
    return () => {
      window.removeEventListener('auth:logout', handleLogout);
    };
  }, [userData, navigate]);

  const { refetch: refetchUserData, data: userDataResponse } = useQuery({
    queryKey: ['userData'],
    queryFn: updateCustomerService,
    retry: false,
    enabled:
      userData?.isAuth === true && userData.on && DateTime.fromISO(userData.on) < DateTime.now().minus({ minutes: 1 }),
  });

  useEffect(() => {
    if (userDataResponse) {
      storeUserData({
        authorization: userDataResponse.token,
        email: userDataResponse.email,
        isAuth: true,
        id: userDataResponse.id,
        name: userDataResponse.name,
        balance: userDataResponse.balance,
        amountLocked: userDataResponse.amountLocked,
        features: userDataResponse.features,
        balancePending: userDataResponse.balancePending,
        accountNumber: userDataResponse.accountNumber,
      });
    }
  }, [userDataResponse]);

  useEffect(() => {
    // Verificar apenas se está na área autenticada
    const isAccessRoute = location.pathname.startsWith('/access');

    if (!isAccessRoute) {
      // Não faz nada se estiver nas rotas públicas
      return;
    }

    // Só verifica autenticação se estiver tentando acessar área protegida
    if (userData.isAuth === true && userData?.authorization) {
      const token = jwt_decode(userData.authorization) as any;
      if (token.exp) {
        const now = new Date();
        const exp = new Date(token.exp * 1000);
        if (now > exp) {
          delAuthStorage();
          setUserData({} as AuthUserData);
          navigate('/auth/sign-in', { replace: true });
        }
      }
    } else {
      // Só redireciona se não estiver autenticado E estiver tentando acessar área protegida
      delAuthStorage();
      setUserData({} as AuthUserData);
      navigate('/auth/sign-in', { replace: true });
    }
  }, [location.pathname, userData.isAuth, userData.authorization, navigate]);

  return (
    <AuthContext.Provider
      value={{
        userData,
        signIn,
        signOut,
        updateUserData: refetchUserData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
